import React, { useState, useEffect, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Camera, Upload, FileImage } from "lucide-react";
import DateInputVN from "@/components/DateInputVN";

export default function MemberModal({ open, onOpenChange, group, member, onSaved }) {
  const isEdit = !!member;
  const [form, setForm] = useState({
    customer_id: null, name: "", phone: "",
    join_date: "", end_date: "", months_paid: 0, notes: "",
    avatar_url: "", receipt_photo_url: "", receipt_status: "none",
    payment_status: "unpaid", order_code: "",
  });
  const [saving, setSaving] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const searchTimeout = useRef(null);
  const avatarInputRef = useRef(null);
  const receiptInputRef = useRef(null);

  useEffect(() => {
    if (open) {
      if (member) {
        setForm({
          customer_id: member.customer_id || null,
          name: member.name || "",
          phone: member.phone || "",
          join_date: member.join_date || "",
          end_date: member.end_date || "",
          months_paid: member.months_paid || 0,
          notes: member.notes || "",
          avatar_url: member.avatar_url || "",
          receipt_photo_url: member.receipt_photo_url || "",
          receipt_status: member.receipt_status || "none",
          payment_status: member.payment_status || "unpaid",
          order_code: member.order_code || "",
        });
      } else {
        const todayDt = new Date();
        const today = todayDt.toISOString().split("T")[0];
        // Thời hạn lùi 1 ngày so với ngày bắt đầu: today + 1 tháng - 1 ngày
        const endDt = new Date(todayDt);
        endDt.setMonth(endDt.getMonth() + 1);
        endDt.setDate(endDt.getDate() - 1);
        const endDate = endDt.toISOString().split("T")[0];
        setForm({ customer_id: null, name: "", phone: "", join_date: today, end_date: endDate, months_paid: 0, notes: "", avatar_url: "", receipt_photo_url: "", receipt_status: "none", payment_status: "unpaid", order_code: "" });
      }
      setSearchResults([]);
      setShowAutocomplete(false);
    }
  }, [open, member]);

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleJoinDateChange = (val) => {
    setField("join_date", val);
    if (!isEdit && val) {
      // Ngày kết thúc lùi 1 ngày so với ngày bắt đầu: val + 1 tháng - 1 ngày
      const d = new Date(val + "T00:00:00");
      d.setMonth(d.getMonth() + 1);
      d.setDate(d.getDate() - 1);
      setField("end_date", d.toISOString().split("T")[0]);
    }
  };

  const handleNameChange = (val) => {
    setField("name", val);
    if (!isEdit && val.length >= 1) {
      clearTimeout(searchTimeout.current);
      searchTimeout.current = setTimeout(async () => {
        try {
          const { data } = await api.get(`/customers/search?q=${encodeURIComponent(val)}`);
          setSearchResults(data.customers || []);
          setShowAutocomplete(true);
        } catch {
          setSearchResults([]);
        }
      }, 300);
    } else {
      setShowAutocomplete(false);
    }
  };

  const selectCustomer = (c) => {
    setForm(f => ({
      ...f,
      customer_id: c.id,
      name: c.name,
      phone: c.phone || f.phone,
      avatar_url: c.avatar_url || f.avatar_url,
    }));
    setShowAutocomplete(false);
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingAvatar(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setField("avatar_url", data.url);
      toast.success("Đã tải ảnh đại diện");
    } catch { toast.error("Lỗi tải ảnh"); }
    finally { setUploadingAvatar(false); }
  };

  const handleReceiptUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingReceipt(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setField("receipt_photo_url", data.url);
      toast.success("Đã tải ảnh biên nhận");
    } catch { toast.error("Lỗi tải ảnh"); }
    finally { setUploadingReceipt(false); }
  };

  const handleExtend = async (months) => {
    if (!member) return;
    try {
      await api.post(`/groups/${group.id}/members/${member.id}/extend`, { months });
      toast.success(`Đã gia hạn thêm ${months} tháng`);
      onOpenChange(false);
      if (onSaved) onSaved();
    } catch (err) {
      toast.error("Lỗi khi gia hạn");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Vui lòng nhập tên thành viên"); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/groups/${group.id}/members/${member.id}`, form);
        toast.success("Đã cập nhật thành viên");
      } else {
        await api.post(`/groups/${group.id}/members`, form);
        toast.success("Đã thêm thành viên");
      }
      onOpenChange(false);
      if (onSaved) onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Có lỗi xảy ra");
    } finally {
      setSaving(false);
    }
  };

  if (!group) return null;
  const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#1e293b] border-[#334155] text-slate-200 max-w-md max-h-[85vh] overflow-y-auto" data-testid="member-modal">
        <DialogHeader>
          <DialogTitle className="text-[#F8FAFC] font-heading">
            {isEdit ? "Sửa thành viên" : "Thêm thành viên"} — {group.name}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Avatar */}
          <div className="flex items-center gap-3">
            <div className="relative w-14 h-14 rounded-full bg-[#0f172a] border border-[#334155] flex items-center justify-center overflow-hidden shrink-0">
              {form.avatar_url ? (
                <img src={form.avatar_url.startsWith("http") ? form.avatar_url : `${API_BASE}${form.avatar_url}`} alt="" className="w-full h-full object-cover" />
              ) : (
                <Camera className="w-5 h-5 text-slate-500" />
              )}
            </div>
            <div className="flex-1">
              <input ref={avatarInputRef} type="file" accept="image/*" className="hidden" onChange={handleAvatarUpload} />
              <Button type="button" variant="outline" size="sm" onClick={() => avatarInputRef.current?.click()} disabled={uploadingAvatar}
                className="border-[#334155] text-slate-300 hover:bg-[#334155] text-xs gap-1" data-testid="upload-avatar-btn">
                <Upload className="w-3 h-3" />
                {uploadingAvatar ? "Đang tải..." : "Ảnh đại diện"}
              </Button>
            </div>
          </div>

          {/* Name with autocomplete */}
          <div className="relative">
            <label className="text-xs text-slate-400 mb-1 block">Tên thành viên *</label>
            <Input value={form.name} onChange={(e) => handleNameChange(e.target.value)}
              placeholder="Nguyễn Văn A" className="bg-[#0f172a] border-[#334155] text-slate-200"
              data-testid="member-name-input"
              onBlur={() => setTimeout(() => setShowAutocomplete(false), 200)} />
            {showAutocomplete && searchResults.length > 0 && (
              <div className="absolute z-50 top-full mt-1 left-0 right-0 bg-[#0f172a] border border-[#334155] rounded-md shadow-lg max-h-40 overflow-y-auto">
                {searchResults.map(c => (
                  <button key={c.id} type="button" onMouseDown={() => selectCustomer(c)}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-[#334155]/50 flex justify-between items-center">
                    <span className="text-slate-200">{c.name}</span>
                    <span className="text-xs text-slate-500">{c.phone}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Phone */}
          <div>
            <label className="text-xs text-slate-400 mb-1 block">SĐT</label>
            <Input value={form.phone} onChange={(e) => setField("phone", e.target.value)}
              placeholder="0901234567" className="bg-[#0f172a] border-[#334155] text-slate-200"
              data-testid="member-phone-input" />
          </div>

          {/* Date range */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Ngày tham gia</label>
              <DateInputVN value={form.join_date} onChange={(v) => handleJoinDateChange(v)}
                className="bg-[#0f172a] border-[#334155] text-slate-200" data-testid="member-join-date" />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Ngày kết thúc</label>
              <DateInputVN value={form.end_date} onChange={(v) => setField("end_date", v)}
                className="bg-[#0f172a] border-[#334155] text-slate-200" data-testid="member-end-date" />
            </div>
          </div>

          {/* Months paid (managed groups) */}
          {group.group_type === "managed" && (
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Số tháng đã đóng</label>
              <Input type="number" value={form.months_paid} onChange={(e) => setField("months_paid", parseInt(e.target.value) || 0)}
                className="bg-[#0f172a] border-[#334155] text-slate-200" />
            </div>
          )}

          {/* Payment status + Order code */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Trạng thái thanh toán</label>
              <Select value={form.payment_status || "unpaid"} onValueChange={(v) => setField("payment_status", v)}>
                <SelectTrigger className="bg-[#0f172a] border-[#334155] text-slate-200" data-testid="payment-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1e293b] border-[#334155]">
                  <SelectItem value="unpaid" className="text-slate-200 focus:bg-[#334155]">Chưa thanh toán</SelectItem>
                  <SelectItem value="paid" className="text-slate-200 focus:bg-[#334155]">Đã thanh toán</SelectItem>
                  <SelectItem value="overdue" className="text-slate-200 focus:bg-[#334155]">Quá hạn</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Mã đơn hàng</label>
              <Input value={form.order_code || (form.payment_status === "paid" && !isEdit ? "Tự tạo khi lưu" : "—")} readOnly
                className="bg-[#0f172a] border-[#334155] text-slate-400 cursor-not-allowed" data-testid="order-code-input" />
              <p className="text-[10px] text-slate-600 mt-0.5">Tự động tạo khi đặt "Đã thanh toán"</p>
            </div>
          </div>

          {/* Extend buttons (edit mode) */}
          {isEdit && (
            <div>
              <label className="text-xs text-slate-400 mb-1.5 block">Gia hạn</label>
              <div className="flex gap-2">
                <Button type="button" size="sm" variant="outline" onClick={() => handleExtend(1)}
                  className="border-lime-500/30 text-lime-400 hover:bg-lime-500/10" data-testid="extend-1m">+1 tháng</Button>
                <Button type="button" size="sm" variant="outline" onClick={() => handleExtend(3)}
                  className="border-lime-500/30 text-lime-400 hover:bg-lime-500/10" data-testid="extend-3m">+3 tháng</Button>
                <Button type="button" size="sm" variant="outline" onClick={() => handleExtend(6)}
                  className="border-lime-500/30 text-lime-400 hover:bg-lime-500/10" data-testid="extend-6m">+6 tháng</Button>
              </div>
            </div>
          )}

          {/* Receipt Photo (edit mode) */}
          {isEdit && (
            <div className="bg-[#0f172a] rounded-lg p-3 border border-[#334155] space-y-2">
              <label className="text-xs text-slate-400 font-medium flex items-center gap-1">
                <FileImage className="w-3.5 h-3.5" /> Biên nhận thanh toán
              </label>
              <div className="flex items-center gap-3">
                {form.receipt_photo_url ? (
                  <img src={form.receipt_photo_url.startsWith("http") ? form.receipt_photo_url : `${API_BASE}${form.receipt_photo_url}`}
                    alt="Biên nhận" className="w-16 h-16 rounded object-cover border border-[#334155]" />
                ) : (
                  <div className="w-16 h-16 rounded bg-[#1e293b] border border-[#334155] flex items-center justify-center">
                    <FileImage className="w-5 h-5 text-slate-600" />
                  </div>
                )}
                <div className="flex-1 space-y-2">
                  <input ref={receiptInputRef} type="file" accept="image/*" className="hidden" onChange={handleReceiptUpload} />
                  <Button type="button" variant="outline" size="sm" onClick={() => receiptInputRef.current?.click()} disabled={uploadingReceipt}
                    className="border-[#334155] text-slate-300 hover:bg-[#334155] text-xs gap-1 w-full" data-testid="upload-receipt-btn">
                    <Upload className="w-3 h-3" />
                    {uploadingReceipt ? "Đang tải..." : "Chọn ảnh biên nhận"}
                  </Button>
                  <Select value={form.receipt_status} onValueChange={(v) => setField("receipt_status", v)}>
                    <SelectTrigger className="bg-[#1e293b] border-[#334155] text-slate-200 h-8 text-xs" data-testid="receipt-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1e293b] border-[#334155]">
                      <SelectItem value="none" className="text-slate-200 focus:bg-[#334155]">Chưa có</SelectItem>
                      <SelectItem value="received" className="text-slate-200 focus:bg-[#334155]">Đã nhận</SelectItem>
                      <SelectItem value="processing" className="text-slate-200 focus:bg-[#334155]">Đang xử lý</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Ghi chú</label>
            <Textarea value={form.notes} onChange={(e) => setField("notes", e.target.value)}
              placeholder="Ghi chú..." className="bg-[#0f172a] border-[#334155] text-slate-200 min-h-[50px]" />
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} className="text-slate-400">Hủy</Button>
            <Button type="submit" disabled={saving} className="bg-lime-500 hover:bg-lime-600 text-[#0f172a] font-semibold" data-testid="member-save-btn">
              {saving ? "Đang lưu..." : (isEdit ? "Cập nhật" : "Thêm")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
